/* eslint-disable no-nested-ternary */
import * as R from 'ramda';
import i18n from 'i18next';
import { FeeDetails, TransactionFee, FooterItem } from './feeConfig';
import {
  GknServiceData,
  Fee,
  ClientFlowType,
} from '../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceData';
import metadataCollector from '../../services/metadata';
import { Action } from '../networkConfig';
import { getNetworkByChainId } from '../../utils/utils';
import { networkConfigs } from '../../common/types';

const SOLANA_RENT = 0.00164;

function centsToUsd(cents: number): string {
  const dollars = cents / 100;
  return dollars.toFixed(2);
}

export function getCurrencyCode(chainName: string, chainNetwork: string): string | undefined {
  switch (chainName) {
    case 'solana':
      return 'SOL';
    case 'ethereum':
      if (chainNetwork) {
        const chainNetworkDetails = getNetworkByChainId(parseInt(chainNetwork, 10), networkConfigs);
        if (chainNetworkDetails) {
          const { symbol } = chainNetworkDetails;
          return symbol;
        }
      }
      return undefined;
    case 'casper':
      return 'CSPR';
    default:
      return undefined;
  }
}

export type LoadFeeConfigParams = {
  fee?: number;
  flowType: string | undefined;
  chainName: string;
  cryptoCurrencyCode: string | undefined;
  gknsData: GknServiceData | null;
  action: string;
  usdToCryptoRate: number | undefined;
  ownerSigns: boolean;
  clientFlowType: ClientFlowType;
};

export const getDisplayFee = (currency: string | undefined, amount: number): string => {
  if (currency !== 'USD') {
    return `${amount.toFixed(6)}`;
  }
  return `$${centsToUsd(amount)}`;
};

export const calculateTransactionFeesFromGknFees = (
  fees: Fee,
  currency: string | undefined,
  action: string,
  chainName: string,
  usdToCryptoRate: number | undefined,
  ownerSigns: boolean,
  clientFlowType: ClientFlowType,
): { transactionFees: Array<TransactionFee>; transactionFooters: Array<FooterItem> } => {
  // Get the fees in USD cents
  let issuanceFeeAmount = fees.amountUSDCents.issuance;
  let refreshFeeAmount = fees.amountUSDCents.refresh;
  let rentAmount = usdToCryptoRate ? SOLANA_RENT * usdToCryptoRate * 100 : 0;
  const isCryptoCurrency = currency !== 'USD';

  if (isCryptoCurrency && usdToCryptoRate && usdToCryptoRate !== 0) {
    // For crypto amounts, convert from USD cents to dollars to crypto
    issuanceFeeAmount = issuanceFeeAmount / 100 / usdToCryptoRate;
    refreshFeeAmount = refreshFeeAmount / 100 / usdToCryptoRate;
    rentAmount = SOLANA_RENT;
  }

  const issuanceFee: TransactionFee = {
    nameI18n:
      clientFlowType === 'document'
        ? 'common.fees.transactionVerificationDocument'
        : 'common.fees.transactionVerification',
    amount: issuanceFeeAmount,
    displayAmount: getDisplayFee(currency, issuanceFeeAmount),
    loading: isCryptoCurrency && R.isNil(usdToCryptoRate),
  };

  const refreshFee: TransactionFee = {
    nameI18n:
      clientFlowType === 'document'
        ? 'common.fees.transactionVerificationDocument'
        : 'common.fees.transactionVerification',
    amount: refreshFeeAmount,
    displayAmount: getDisplayFee(currency, refreshFeeAmount),
    loading: isCryptoCurrency && R.isNil(usdToCryptoRate),
  };

  // Rent is SOL-based, so we need to convert to USD
  const solanaRentFee: TransactionFee = {
    nameI18n: 'common.fees.transactionSolanaRent',
    amount: rentAmount,
    displayAmount: getDisplayFee(currency, rentAmount),
    loading: currency !== 'USD' && R.isNil(usdToCryptoRate),
  };
  // Gas is for EVM only
  const evmGasFee: TransactionFee = {
    nameI18n: 'common.fees.transactionEvmGasFee',
    amount: 0,
    displayAmount: i18n.t('common.fees.transactionEvmGasFeeDescription'),
    loading: false,
  };
  const verificationFooter: FooterItem = {
    nameI18n:
      clientFlowType === 'document'
        ? 'common.fees.transactionVerificationDocumentFooter'
        : 'common.fees.transactionVerificationFooter',
  };
  const evmGasFooter: FooterItem = {
    nameI18n: 'common.fees.transactionEvmGasFooter',
  };
  const solanaRentFooter: FooterItem = {
    nameI18n: 'common.fees.transactionSolanaRentFooter',
  };
  // Add non-zero issuance or refresh based on action, and add rent if solana issuance
  const transactionFees: Array<TransactionFee> = [];
  const transactionFooters: Array<FooterItem> = [];
  let footerCount = 1;

  if (action === Action.ISSUANCE) {
    if (issuanceFee.amount !== 0) {
      transactionFees.push({ ...issuanceFee, footerNumber: footerCount });
      transactionFooters.push({ ...verificationFooter, number: footerCount });
      footerCount += 1;
    }
    if (chainName === 'solana') {
      transactionFees.push({ ...solanaRentFee, footerNumber: footerCount });
      transactionFooters.push({ ...solanaRentFooter, number: footerCount });
      footerCount += 1;
    }
  } else if (refreshFee.amount !== 0) {
    transactionFees.push({ ...refreshFee, footerNumber: footerCount });
    transactionFooters.push({ ...verificationFooter, number: footerCount });
    footerCount += 1;
  }
  // For ethereum, we always show the gas fee when owner signs
  if (chainName === 'ethereum' && ownerSigns) {
    transactionFees.push({ ...evmGasFee, footerNumber: footerCount });
    transactionFooters.push({ ...evmGasFooter, number: footerCount });
    footerCount += 1;
  }
  return { transactionFees, transactionFooters };
};

export const calculateTransactionFees = (
  fee: number,
  currency: string | undefined,
  action: string,
  chainName: string,
  usdToCryptoRate: number | undefined,
  ownerSigns: boolean,
  clientFlowType: ClientFlowType,
): { transactionFees: Array<TransactionFee>; transactionFooters: Array<FooterItem> } => {
  // Get the fees in USD cents
  let feeAmount = fee;
  let rentAmount = usdToCryptoRate ? SOLANA_RENT * usdToCryptoRate * 100 : 0;
  const isCryptoCurrency = currency !== 'USD';

  if (isCryptoCurrency && usdToCryptoRate && usdToCryptoRate !== 0) {
    // For crypto amounts, convert from USD cents to dollars to crypto
    feeAmount = feeAmount / 100 / usdToCryptoRate;
    rentAmount = SOLANA_RENT;
  }

  const actionFee: TransactionFee = {
    nameI18n:
      clientFlowType === 'document'
        ? 'common.fees.transactionVerificationDocument'
        : 'common.fees.transactionVerification',
    amount: feeAmount,
    displayAmount: getDisplayFee(currency, feeAmount),
    loading: isCryptoCurrency && R.isNil(usdToCryptoRate),
  };

  // Rent is SOL-based, so we need to convert to USD
  const solanaRentFee: TransactionFee = {
    nameI18n: 'common.fees.transactionSolanaRent',
    amount: rentAmount,
    displayAmount: getDisplayFee(currency, rentAmount),
    loading: currency !== 'USD' && R.isNil(usdToCryptoRate),
  };

  // Gas is for EVM only
  const evmGasFee: TransactionFee = {
    nameI18n: 'common.fees.transactionEvmGasFee',
    amount: 0,
    displayAmount: i18n.t('common.fees.transactionEvmGasFeeDescription'),
    loading: false,
  };

  const verificationFooter: FooterItem = {
    nameI18n:
      clientFlowType === 'document'
        ? 'common.fees.transactionVerificationDocumentFooter'
        : 'common.fees.transactionVerificationFooter',
  };

  const evmGasFooter: FooterItem = {
    nameI18n: 'common.fees.transactionEvmGasFooter',
  };

  const solanaRentFooter: FooterItem = {
    nameI18n: 'common.fees.transactionSolanaRentFooter',
  };

  // Add non-zero issuance or refresh based on action, and add rent if solana issuance
  const transactionFees: Array<TransactionFee> = [];
  const transactionFooters: Array<FooterItem> = [];
  let footerCount = 1;
  if (actionFee.amount !== 0) {
    if (action === Action.ISSUANCE) {
      transactionFees.push({ ...actionFee, footerNumber: footerCount });
    } else if (action === Action.REFRESH) {
      transactionFees.push({ ...actionFee, footerNumber: footerCount });
    }
    transactionFooters.push({ ...verificationFooter, number: footerCount });
    footerCount += 1;

    if (action === Action.ISSUANCE && chainName === 'solana') {
      transactionFees.push({ ...solanaRentFee, footerNumber: footerCount });
      transactionFooters.push({ ...solanaRentFooter, number: footerCount });
      footerCount += 1;
    }
  }
  // For ethereum, we always show the gas fee when owner signs
  if (chainName === 'ethereum' && ownerSigns) {
    transactionFees.push({ ...evmGasFee, footerNumber: footerCount });
    transactionFooters.push({ ...evmGasFooter, number: footerCount });
    footerCount += 1;
  }

  return { transactionFees, transactionFooters };
};

// Zero fees means we don't want to show anything fee-related
const hasZeroGknFees = (fees: Fee, action: string, chainName: string, ownerSigns: boolean): boolean => {
  // Ethereum: we want to show gas for both issuance and refresh when owner signs
  if (chainName === 'ethereum' && ownerSigns) {
    return false;
  }
  // Solana: we want to show rent for issuance, but not refresh when owner signs
  if (chainName === 'solana' && ownerSigns && action === Action.ISSUANCE) {
    return false;
  }
  if (action === Action.ISSUANCE) {
    return fees.amountUSDCents.issuance === 0;
  }
  return fees.amountUSDCents.refresh === 0;
};

// Zero fees means we don't want to show anything fee-related
const hasZeroFees = (chainName: string, ownerSigns: boolean, fee?: number): boolean => {
  // Ethereum: we want to show gas for both issuance and refresh when owner signs
  if (chainName === 'ethereum' && ownerSigns) {
    return false;
  }
  // Solana: we want to show rent for issuance, but not refresh when owner signs
  if (chainName === 'solana' && ownerSigns) {
    return false;
  }
  return fee === 0;
};

// Returns the formatted and unformatted total fees in USD and crypto
function calculateTotalFees(
  usdTransactionFees: Array<{ amount: number }>,
  cryptoTransactionFees: Array<{ amount: number }>,
  cryptoCurrencyCode: string,
  usdToCryptoRate: number | undefined,
  chainName: string,
  ownerSigns: boolean,
) {
  // We show "Gas + " before the total for evm with owner signs
  const showGas = chainName === 'ethereum' && ownerSigns;
  const gasText = showGas ? i18n.t('common.fees.transactionEvmGasTotal') || 'common.fees.transactionEvmGasTotal' : '';
  const usdCurrencyDisplay = 'USD'.concat(showGas ? `${gasText}` : '');
  const cryptoCurrencyDisplay = cryptoCurrencyCode.concat(showGas ? `${gasText}` : '');
  const totalUsdFees = centsToUsd(R.pipe(R.map(R.prop('amount')), R.sum)(usdTransactionFees));
  const totalUsdFeesDisplay = `$${totalUsdFees}`;
  const totalCryptoFees = R.pipe(R.map(R.prop('amount')), R.sum)(cryptoTransactionFees).toFixed(6);
  const totalCryptoFeesDisplay = R.isNil(usdToCryptoRate) ? undefined : `${totalCryptoFees}`;
  return {
    totalUsdFeesDisplay,
    totalCryptoFeesDisplay,
    totalUsdFees,
    totalCryptoFees,
    usdCurrencyDisplay,
    cryptoCurrencyDisplay,
  };
}

const loadFeeConfig = ({
  fee,
  chainName,
  cryptoCurrencyCode,
  gknsData,
  action,
  usdToCryptoRate,
  ownerSigns,
  clientFlowType,
  flowType,
}: LoadFeeConfigParams): FeeDetails | null => {
  const gknFees: Fee | null = R.pathOr(null, ['chains', chainName, 'fees'], gknsData);
  // This is a temporary solution. We are currently showing the same screen for refresh and issuance
  // https://civicteam.atlassian.net/browse/CPASS-2873
  const determineFeeAction = () => {
    if (action === Action.ISSUANCE || action === Action.REFRESH || action === Action.TOKEN_REFRESH_IN_REVIEW) {
      return action;
    }

    if (flowType === 'refresh') {
      return Action.REFRESH;
    }

    return Action.ISSUANCE;
  };

  // Action can only be issuance or refresh, else default to issuance for backwards compatibility
  const feeAction: string = determineFeeAction();
  // Without fees, we can't calculate anything
  // Or when fees are zero, we don't want to show anything
  let feeDetails: FeeDetails;

  if (R.isNil(fee)) {
    // If we don't have a fee from the query parms, default to the gkn fees, first checking for null/zero
    if (R.isNil(gknFees) || hasZeroGknFees(gknFees, feeAction, chainName, ownerSigns)) {
      return null;
    }
    const { transactionFees: usdTransactionFees, transactionFooters: usdTransactionFooter } =
      calculateTransactionFeesFromGknFees(
        gknFees,
        'USD',
        feeAction,
        chainName,
        usdToCryptoRate,
        ownerSigns,
        clientFlowType,
      );
    const { transactionFees: cryptoTransactionFees, transactionFooters: cryptoTransactionFooter } =
      calculateTransactionFeesFromGknFees(
        gknFees,
        cryptoCurrencyCode,
        feeAction,
        chainName,
        usdToCryptoRate,
        ownerSigns,
        clientFlowType,
      );
    const {
      totalUsdFees,
      totalCryptoFees,
      totalCryptoFeesDisplay,
      totalUsdFeesDisplay,
      usdCurrencyDisplay,
      cryptoCurrencyDisplay,
    } = calculateTotalFees(
      usdTransactionFees,
      cryptoTransactionFees,
      cryptoCurrencyCode || '',
      usdToCryptoRate,
      chainName,
      ownerSigns,
    );
    metadataCollector.updateMetadata({
      transactionFee: `${totalUsdFees} USD / ${totalCryptoFees} ${cryptoCurrencyCode || ''}`,
    });

    feeDetails = {
      usdToCryptoRate,
      usd: {
        currencyOnly: 'USD',
        currency: usdCurrencyDisplay,
        transactionFees: usdTransactionFees,
        footers: usdTransactionFooter,
        total: totalUsdFeesDisplay,
      },
      crypto: {
        currencyOnly: cryptoCurrencyCode,
        currency: cryptoCurrencyDisplay || '',
        total: totalCryptoFeesDisplay,
        transactionFees: cryptoTransactionFees,
        footers: cryptoTransactionFooter,
      },
    };
  } else {
    // Else we look at the fee from the query params
    if (hasZeroFees(chainName, ownerSigns, fee)) {
      return null;
    }
    const { transactionFees: usdTransactionFees, transactionFooters: usdTransactionFooter } = calculateTransactionFees(
      fee,
      'USD',
      feeAction,
      chainName,
      usdToCryptoRate,
      ownerSigns,
      clientFlowType,
    );
    const { transactionFees: cryptoTransactionFees, transactionFooters: cryptoTransactionFooter } =
      calculateTransactionFees(
        fee,
        cryptoCurrencyCode,
        feeAction,
        chainName,
        usdToCryptoRate,
        ownerSigns,
        clientFlowType,
      );
    const {
      totalUsdFees,
      totalCryptoFees,
      totalCryptoFeesDisplay,
      totalUsdFeesDisplay,
      usdCurrencyDisplay,
      cryptoCurrencyDisplay,
    } = calculateTotalFees(
      usdTransactionFees,
      cryptoTransactionFees,
      cryptoCurrencyCode || '',
      usdToCryptoRate,
      chainName,
      ownerSigns,
    );

    metadataCollector.updateMetadata({
      transactionFee: `${totalUsdFees} USD / ${totalCryptoFees} ${cryptoCurrencyCode || ''}`,
    });

    feeDetails = {
      usdToCryptoRate,
      usd: {
        currency: usdCurrencyDisplay,
        transactionFees: usdTransactionFees,
        footers: usdTransactionFooter,
        total: totalUsdFeesDisplay,
      },
      crypto: {
        currency: cryptoCurrencyDisplay || '',
        total: totalCryptoFeesDisplay,
        transactionFees: cryptoTransactionFees,
        footers: cryptoTransactionFooter,
      },
    };
  }

  return feeDetails;
};

export default loadFeeConfig;
